// crewbit-admin/src/lib/invoiceBulkMutations.ts
//
// §Money.HardeningBulkPass2 Phase 3 — bulk wrappers for the 4 NEW SECDEF
// bulk RPCs (Migration 300). Per Decision #592: admin-only UI surface.
// Per Decision #591: bulk-RPC-per-action — saves N round-trips + cleaner
// idempotency + matches established Money RPC wrapper patterns.
//
// Extracted to a new file per Golden Rule #14 — invoiceMutations.ts is at
// 491/500 lines pre-Phase 3; mirrors the §T.85 recordPaymentMutations.ts +
// §T.87 refundStripeMutation.ts precedent.

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { MONEY_MUTATION_EVENT } from '../hooks/useFinancialSummary';

/**
 * Per-invoice failure entry as returned in the bulk RPC envelope's `failed[]`.
 * The `error` shape mirrors single-action RPC error envelopes (`{code, ...}`)
 * for envelope-shaped failures, OR `{code:'RPC_ERROR', message}` for raises
 * caught by the bulk RPC's per-iteration BEGIN/EXCEPTION block.
 */
export interface BulkInvoiceFailure {
  invoice_id: string;
  error: { code?: string; message?: string; meta?: unknown } | unknown;
}

/**
 * Standard envelope returned by all 4 bulk RPCs on the auth-gates-passed path.
 * `success` = true means the bulk operation completed; per-invoice success is
 * tracked by `success_count` / `failed_count` / `failed[]`.
 *
 * Auth-gate failures (NO_ACTIVE_BUSINESS / STALE_PUBLIC_USER_ID / FORBIDDEN)
 * are surfaced via the standard `MoneyRpcResult` failure path with the error
 * code at the envelope's top level (NOT in `failed[]`).
 */
export interface BulkInvoiceEnvelope {
  total: number;
  success_count: number;
  failed_count: number;
  failed: BulkInvoiceFailure[];
}

export async function deleteDraftInvoicesBulk(
  invoiceIds: string[],
  idempotencyKey?: string,
): Promise<MoneyRpcResult<BulkInvoiceEnvelope>> {
  const result = await callMoneyRpc<BulkInvoiceEnvelope>(
    'delete_draft_invoices_bulk',
    {
      p_invoice_ids: invoiceIds,
      p_idempotency_key: idempotencyKey ?? null,
    },
  );
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export async function cancelInvoicesBulk(
  invoiceIds: string[],
  reason: string,
  idempotencyKey?: string,
): Promise<MoneyRpcResult<BulkInvoiceEnvelope>> {
  const result = await callMoneyRpc<BulkInvoiceEnvelope>(
    'cancel_invoices_bulk',
    {
      p_invoice_ids: invoiceIds,
      p_reason: reason,
      p_idempotency_key: idempotencyKey ?? null,
    },
  );
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export async function sendReminderBulk(
  invoiceIds: string[],
  channel: string,
  idempotencyKey?: string,
): Promise<MoneyRpcResult<BulkInvoiceEnvelope>> {
  const result = await callMoneyRpc<BulkInvoiceEnvelope>(
    'send_reminder_bulk',
    {
      p_invoice_ids: invoiceIds,
      p_channel: channel,
      p_idempotency_key: idempotencyKey ?? null,
    },
  );
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export async function markInvoicesPaidBulk(
  invoiceIds: string[],
  method: string,
  idempotencyKey?: string,
): Promise<MoneyRpcResult<BulkInvoiceEnvelope>> {
  const result = await callMoneyRpc<BulkInvoiceEnvelope>(
    'mark_invoices_paid_bulk',
    {
      p_invoice_ids: invoiceIds,
      p_method: method,
      p_idempotency_key: idempotencyKey ?? null,
    },
  );
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}
